import { useState, useEffect } from "react";
import { View, StyleSheet, useWindowDimensions, ActivityIndicator } from "react-native";
import { useLocalSearchParams, router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Image, ImageSource } from "expo-image";
import useAuth from "@/hooks/useAuth";
import { timecardService } from "@/services/timecardService";

type ClockInResult = {
  success: boolean;
  timecard_id?: string;
  clock_in_time?: string;
  user?: { id: string; name: string };
  shift?: { id: string; name: string; location: string };
  position?: string;
  error?: string;
};

export default function ClockInConfirmScreen() {
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const { shift, email } = useLocalSearchParams<{ shift: string; email?: string }>();
  const { session } = useAuth();
  const [logo] = useState<ImageSource>(require("@/assets/images/rockforce-logo-rf-yellow.svg"));
  const [loading, setLoading] = useState(true);
  const [clockInData, setClockInData] = useState<ClockInResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const showLogo = width <= 900;

  // Perform clock-in on mount
  useEffect(() => {
    const performClockIn = async () => {
      try {
        const result = await timecardService.clockIn(shift, email || undefined);
        if (result.success) {
          setClockInData(result);
        } else {
          setError(result.error || "Failed to clock in");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    };

    performClockIn();
  }, [shift, email]);

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.loadingContent}>
          <ActivityIndicator size="large" color={colors.primary} />
          <ThemedText style={[styles.loadingText, { color: colors.text }]}>Clocking you in...</ThemedText>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.content}>
          {showLogo && (
            <View style={styles.logoSection}>
              <Image source={[logo]} style={styles.logo} />
              <ThemedText style={[styles.logoTitle, { color: colors.text }]}>RockForce</ThemedText>
            </View>
          )}

          <View style={[styles.errorCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={[styles.errorCircle, { backgroundColor: colors.ruby?.background || "#fee2e2" }]}>
              <MaterialCommunityIcons name="alert-circle" size={64} color={colors.ruby?.text || "#dc2626"} />
            </View>

            <ThemedText style={[styles.errorTitle, { color: colors.text }]}>Clock-In Failed</ThemedText>
            <ThemedText style={[styles.errorMessage, { color: colors.textSecondary }]}>{error}</ThemedText>

            <Button
              id="try-again"
              text="Try Again"
              onPress={() => router.back()}
              icon="arrow-left"
              iconSize={20}
              iconColor="white"
              style={[styles.tryAgainButton, { backgroundColor: colors.primary }]}
              textStyle={styles.tryAgainButtonText}
            />
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.content}>
        {/* Logo Section */}
        {showLogo && (
          <View style={styles.logoSection}>
            <Image source={[logo]} style={styles.logo} />
            <ThemedText style={[styles.logoTitle, { color: colors.text }]}>RockForce</ThemedText>
          </View>
        )}

        {/* Success Card */}
        <View style={[styles.successCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={[styles.checkCircle, { backgroundColor: colors.emerald?.background || "#d1fae5" }]}>
            <MaterialCommunityIcons name="check-circle" size={64} color={colors.emerald?.text || "#10b981"} />
          </View>

          <ThemedText style={[styles.successTitle, { color: colors.text }]}>You&apos;re Clocked In!</ThemedText>
          <ThemedText style={[styles.successSubtitle, { color: colors.textSecondary }]}>
            Your attendance has been recorded
          </ThemedText>

          {/* Shift Details */}
          <View
            style={[styles.detailsCard, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}
          >
            <View style={styles.detailRow}>
              <MaterialCommunityIcons name="account" size={20} color={colors.textSecondary} />
              <View style={styles.detailText}>
                <ThemedText style={[styles.detailLabel, { color: colors.textSecondary }]}>User</ThemedText>
                <ThemedText style={[styles.detailValue, { color: colors.text }]}>
                  {clockInData?.user?.name || "Unknown"}
                </ThemedText>
              </View>
            </View>

            <View style={[styles.divider, { backgroundColor: colors.border }]} />

            <View style={styles.detailRow}>
              <MaterialCommunityIcons name="calendar" size={20} color={colors.textSecondary} />
              <View style={styles.detailText}>
                <ThemedText style={[styles.detailLabel, { color: colors.textSecondary }]}>Shift</ThemedText>
                <ThemedText style={[styles.detailValue, { color: colors.text }]}>
                  {clockInData?.shift?.name || "Unknown Shift"}
                </ThemedText>
              </View>
            </View>

            {clockInData?.position && (
              <>
                <View style={[styles.divider, { backgroundColor: colors.border }]} />
                <View style={styles.detailRow}>
                  <MaterialCommunityIcons name="briefcase" size={20} color={colors.textSecondary} />
                  <View style={styles.detailText}>
                    <ThemedText style={[styles.detailLabel, { color: colors.textSecondary }]}>Position</ThemedText>
                    <ThemedText style={[styles.detailValue, { color: colors.text }]}>{clockInData.position}</ThemedText>
                  </View>
                </View>
              </>
            )}

            <View style={[styles.divider, { backgroundColor: colors.border }]} />

            <View style={styles.detailRow}>
              <MaterialCommunityIcons name="clock-outline" size={20} color={colors.textSecondary} />
              <View style={styles.detailText}>
                <ThemedText style={[styles.detailLabel, { color: colors.textSecondary }]}>Clock-in Time</ThemedText>
                <ThemedText style={[styles.detailValue, { color: colors.text }]}>
                  {clockInData?.clock_in_time
                    ? new Date(clockInData.clock_in_time).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </ThemedText>
              </View>
            </View>

            {clockInData?.shift?.location && (
              <>
                <View style={[styles.divider, { backgroundColor: colors.border }]} />
                <View style={styles.detailRow}>
                  <MaterialCommunityIcons name="map-marker" size={20} color={colors.textSecondary} />
                  <View style={styles.detailText}>
                    <ThemedText style={[styles.detailLabel, { color: colors.textSecondary }]}>Location</ThemedText>
                    <ThemedText style={[styles.detailValue, { color: colors.text }]}>
                      {clockInData.shift.location}
                    </ThemedText>
                  </View>
                </View>
              </>
            )}
          </View>
        </View>

        {/* Info Card or Portal Button */}
        {session ? (
          <Button
            id="back-to-portal"
            text="Back to RockForce Portal"
            onPress={() => router.replace("/")}
            icon="home"
            iconSize={20}
            iconColor="white"
            style={[styles.portalButton, { backgroundColor: colors.primary }]}
            textStyle={styles.portalButtonText}
          />
        ) : (
          <View style={[styles.infoCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <MaterialCommunityIcons name="information-outline" size={20} color={colors.textSecondary} />
            <ThemedText style={[styles.infoText, { color: colors.textSecondary }]}>
              You can close this window. Have a great shift!
            </ThemedText>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContent: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
  },
  loadingText: {
    fontSize: 18,
    fontWeight: "500",
  },
  content: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    width: "100%",
    maxWidth: 400,
    alignSelf: "center",
  },
  logoSection: {
    alignItems: "center",
    marginBottom: 32,
  },
  logo: {
    height: 48,
    width: 48,
    marginBottom: 8,
  },
  logoTitle: {
    fontSize: 20,
    fontWeight: "800",
    letterSpacing: 2,
  },
  successCard: {
    width: "100%",
    borderRadius: 16,
    borderWidth: 1,
    padding: 24,
    alignItems: "center",
    marginBottom: 16,
  },
  checkCircle: {
    width: 100,
    height: 100,
    borderRadius: 50,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  successTitle: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 4,
  },
  successSubtitle: {
    fontSize: 14,
    marginBottom: 24,
  },
  errorCard: {
    width: "100%",
    borderRadius: 16,
    borderWidth: 1,
    padding: 24,
    alignItems: "center",
    marginBottom: 16,
  },
  errorCircle: {
    width: 100,
    height: 100,
    borderRadius: 50,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 8,
  },
  errorMessage: {
    fontSize: 14,
    textAlign: "center",
    marginBottom: 24,
  },
  tryAgainButton: {
    width: "100%",
    borderRadius: 10,
    minHeight: 50,
  },
  tryAgainButtonText: {
    color: "white",
    fontSize: 17,
    fontWeight: "600",
  },
  detailsCard: {
    width: "100%",
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingVertical: 8,
  },
  detailText: {
    flex: 1,
  },
  detailLabel: {
    fontSize: 12,
  },
  detailValue: {
    fontSize: 16,
    fontWeight: "500",
  },
  divider: {
    height: 1,
    width: "100%",
  },
  infoCard: {
    width: "100%",
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  infoText: {
    fontSize: 14,
    flex: 1,
  },
  portalButton: {
    width: "100%",
    borderRadius: 10,
    minHeight: 50,
  },
  portalButtonText: {
    color: "white",
    fontSize: 17,
    fontWeight: "600",
  },
});
